import "../styles/AssetCard.css";
import { locationIcon, meterIcon, wrenchIcon } from "../utils/svgs";

const AssetSourceTypeEnum = {
  Vehicle: "vehicle",
  Equipment: "equipment",
  Tyre: "tyre",
};

const getNormalizedAssetType = (workOrder) => {
  const normalizedAssetType = String(workOrder?.asset_type || "")
    .trim()
    .toLowerCase();

  if (Object.values(AssetSourceTypeEnum).includes(normalizedAssetType)) {
    return normalizedAssetType;
  }

  if (workOrder?.equipment || workOrder?.equipment_id) {
    return AssetSourceTypeEnum.Equipment;
  }

  if (workOrder?.tyre || workOrder?.tyre_id) {
    return AssetSourceTypeEnum.Tyre;
  }

  if (workOrder?.vehicle || workOrder?.vehicle_id || workOrder?.asset_id) {
    return AssetSourceTypeEnum.Vehicle;
  }

  return "";
};

const getAssetTypeLabel = (assetType) => {
  if (assetType === AssetSourceTypeEnum.Equipment) return "Equipment";
  if (assetType === AssetSourceTypeEnum.Tyre) return "Tyre";
  return "Vehicle";
};

export const getWorkOrderAssetType = (workOrder) =>
  getNormalizedAssetType(workOrder);

export const getWorkOrderAssetTypeLabel = (workOrder) =>
  getAssetTypeLabel(getNormalizedAssetType(workOrder));

const getAssetData = (workOrder, assetType) => {
  if (assetType === AssetSourceTypeEnum.Equipment) {
    return workOrder?.equipment || null;
  }

  if (assetType === AssetSourceTypeEnum.Tyre) {
    return workOrder?.tyre || null;
  }

  return workOrder?.vehicle || null;
};

const pickFirstValue = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === "" || candidate === null || candidate === undefined) {
      continue;
    }

    return candidate;
  }

  return "";
};

export const getWorkOrderAssetLabel = (
  workOrder,
  assetType = getNormalizedAssetType(workOrder)
) => {
  const assetData = getAssetData(workOrder, assetType);

  if (assetType === AssetSourceTypeEnum.Equipment) {
    return pickFirstValue(
      assetData?.equipment_id,
      assetData?.title,
      assetData?.name,
      assetData?.serial_number,
      workOrder?.asset_id
    );
  }

  if (assetType === AssetSourceTypeEnum.Tyre) {
    return pickFirstValue(
      assetData?.tyres_id,
      assetData?.tyre_id,
      assetData?.serial_number,
      assetData?.system_id,
      workOrder?.asset_id
    );
  }

  return pickFirstValue(
    assetData?.registration_number,
    assetData?.registration_no,
    assetData?.vehicle_number,
    assetData?.asset_id,
    workOrder?.asset_id
  );
};

const getAssetSubtitle = (assetData, assetType) => {
  if (!assetData) return "-";

  if (assetType === AssetSourceTypeEnum.Equipment) {
    return pickFirstValue(
      [assetData?.manufacturer?.name, assetData?.model?.name]
        .filter(Boolean)
        .join(" "),
      [assetData?.manufacturer_name, assetData?.model_name]
        .filter(Boolean)
        .join(" "),
      assetData?.description,
      assetData?.serial_number
    );
  }

  if (assetType === AssetSourceTypeEnum.Tyre) {
    return pickFirstValue(
      [assetData?.brand?.name, assetData?.model?.name].filter(Boolean).join(" "),
      [assetData?.size, assetData?.serial_number].filter(Boolean).join(" - "),
      assetData?.serial_number,
      assetData?.description
    );
  }

  return pickFirstValue(
    [assetData?.vehicle_brand?.title, assetData?.vehicle_model?.title]
      .filter(Boolean)
      .join(" "),
    [assetData?.brand, assetData?.model].filter(Boolean).join(" "),
    assetData?.model
  );
};

const getAssetMeterValue = (assetData, workOrder) =>
  pickFirstValue(
    assetData?.odm,
    assetData?.odo_meter,
    assetData?.usage_meter_reading,
    assetData?.usageMeterReading,
    assetData?.meter_reading,
    assetData?.distance,
    workOrder?.odo_meter
  );

const getAssetLocationValue = (assetData, workOrder, assetType) => {
  if (assetType === AssetSourceTypeEnum.Tyre) {
    return pickFirstValue(
      assetData?.warehouse?.title,
      assetData?.warehouse?.name,
      assetData?.warehouse_name,
      assetData?.warehouse_id ? `Warehouse ${assetData.warehouse_id}` : "",
      workOrder?.vendor?.name
    );
  }

  return pickFirstValue(
    assetData?.rto,
    assetData?.location,
    assetData?.current_location,
    assetData?.branch?.title,
    assetData?.warehouse?.title,
    workOrder?.vendor?.name
  );
};

const getWorkOrderServices = (workOrder, assetData) => {
  const hasCurrentService =
    Boolean(workOrder?.service_id) || Array.isArray(workOrder?.service_line_items);
  const currentService = hasCurrentService ? [workOrder] : [];

  const mergedServices = [
    ...currentService,
    ...(Array.isArray(workOrder?.services) ? workOrder.services : []),
    ...(Array.isArray(workOrder?.vehicle_services) ? workOrder.vehicle_services : []),
    ...(Array.isArray(assetData?.services) ? assetData.services : []),
  ];

  const uniqueServices = [];
  const seenServices = new Set();

  mergedServices.forEach((service) => {
    const serviceKey = String(
      pickFirstValue(service?.id, service?.service_id, service?.maintenances_service_id)
    );

    if (!serviceKey || seenServices.has(serviceKey)) {
      return;
    }

    seenServices.add(serviceKey);
    uniqueServices.push(service);
  });

  return uniqueServices;
};

const getWorkOrderIssues = (workOrder, assetData) => {
  const serviceIssues = getWorkOrderServices(workOrder, assetData).flatMap(
    (service) => (Array.isArray(service?.service_issues) ? service.service_issues : [])
  );
  const mergedIssues = [
    ...serviceIssues,
    ...(Array.isArray(workOrder?.issues) ? workOrder.issues : []),
    ...(Array.isArray(assetData?.issues) ? assetData.issues : []),
  ];

  const uniqueIssues = [];
  const seenIssues = new Set();

  mergedIssues.forEach((issue) => {
    const issueKey = String(
      pickFirstValue(issue?.id, issue?.service_issue_id, issue?.summery, issue?.description)
    );

    if (!issueKey || seenIssues.has(issueKey)) {
      return;
    }

    seenIssues.add(issueKey);
    uniqueIssues.push(issue);
  });

  return uniqueIssues;
};

const AssetCard = ({ workOrder }) => {
  const assetType = getNormalizedAssetType(workOrder);
  const assetData = getAssetData(workOrder, assetType);
  const assetTypeLabel = getAssetTypeLabel(assetType);
  const assetLabel = getWorkOrderAssetLabel(workOrder, assetType) || "-";
  const assetSubtitle = getAssetSubtitle(assetData, assetType) || "-";
  const assetMeter = getAssetMeterValue(assetData, workOrder);
  const assetLocation = getAssetLocationValue(assetData, workOrder, assetType) || "-";
  const services = getWorkOrderServices(workOrder, assetData);
  const issues = getWorkOrderIssues(workOrder, assetData);
  return (
    <div className="asset_card_wrapper">
      <div className="asset_card_header">
        <div className="asset_card_badge">
          {assetTypeLabel.slice(0, 3)}
        </div>
        <div className="asset_card_info">
          <h3>{assetLabel}</h3>
          <p title={assetSubtitle}>{assetSubtitle}</p>
        </div>
      </div>

      <div className="asset_card_meta">
        <div className="asset_card_meta_item">
          {meterIcon}
          {assetMeter !== "" && assetMeter !== null && assetMeter !== undefined
            ? `${assetMeter} Km`
            : "-"}
        </div>
        <div className="asset_card_meta_item">
          {locationIcon}
          {assetLocation}
        </div>
      </div>

      <div className="asset_card_section">
        <div className="asset_card_section_header">
          <h6>Service Entries</h6>
          <span className="asset_card_section_count">{services.length} items</span>
        </div>
        {services.length === 0 ? (
          <p className="asset_card_empty">No Service Entry</p>
        ) : (
          <div>
            <ul className="asset_card_services_list">
              {services.map((service, idx) => (
                <li key={`${service?.id || service?.service_id || idx}`}>
                  {service?.service_id || "-"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="asset_card_section">
        <div className="asset_card_section_header">
          <h6>Issues</h6>
          <span className="asset_card_section_count">{issues.length} items</span>
        </div>
        <div>
          <ul className="asset_card_issues_list">
            {issues.length > 0 ? (
              issues.map((issue, idx) => (
                <li key={`${issue?.id || issue?.service_issue_id || idx}`}>
                  <span>
                    {wrenchIcon} {issue?.summery || issue?.description || "-"}
                  </span>
                </li>
              ))
            ) : (
              <li className="asset_card_empty">-</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssetCard;
